import React from 'react';
import Input from '../common/Input';
import Select from '../common/Select';
import { useContract } from '../../context/ContractContext';

const FinancialForm = () => {
  const { contractData, updateContractData } = useContract();
  const { financial = {} } = contractData;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const processedValue = type === 'number' ? parseFloat(value) || 0 : value;
    
    updateContractData('financial', {
      ...financial,
      [name]: processedValue
    });
  };

  const adjustmentOptions = [
    { value: 'igpm', label: 'IGP-M' },
    { value: 'ipca', label: 'IPCA' },
    { value: 'incc', label: 'INCC' },
    { value: 'iga', label: 'IGA' },
    { value: 'fixo', label: 'Valor Fixo (sem reajuste)' }
  ];

  const dueDateOptions = Array.from({ length: 28 }, (_, i) => ({
    value: i + 1,
    label: `Dia ${i + 1}`
  }));

  const durationOptions = [
    { value: 6, label: '6 meses' },
    { value: 12, label: '1 ano' },
    { value: 24, label: '2 anos' },
    { value: 30, label: '30 meses' },
    { value: 36, label: '3 anos' },
    { value: 60, label: '5 anos' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const handleFeeChange = (index, field, value) => {
    const fees = [...(financial.fees || [])];
    fees[index] = { ...fees[index], [field]: value };
    
    updateContractData('financial', { 
      ...financial,
      fees 
    });
  };

  const addFee = () => {
    const fees = [...(financial.fees || [])];
    fees.push({ name: '', value: 0, description: '' });
    
    updateContractData('financial', { 
      ...financial,
      fees 
    });
  };

  const removeFee = (index) => {
    const fees = financial.fees.filter((_, i) => i !== index);
    
    updateContractData('financial', { 
      ...financial,
      fees 
    });
  };

  const getTotalValue = () => {
    const rent = parseFloat(financial.rentValue) || 0;
    const feesTotal = (financial.fees || []).reduce((sum, fee) => sum + (parseFloat(fee.value) || 0), 0);
    return rent + feesTotal;
  };

  // Garantir que o campo value seja salvo corretamente
  const handleValueChange = (fieldName, value) => {
    updateContractData('financial', {
      ...financial,
      [fieldName]: parseFloat(value) || 0,
      // Garantir que sempre tenha o campo value para validação
      value: fieldName === 'rentValue' ? (parseFloat(value) || 0) : (financial.value || financial.rentValue || 0)
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-dark-900 mb-6">
          Condições Financeiras
        </h2>

        {/* Valores principais */}
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold text-dark-900 mb-4">
            Valores do Contrato
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={contractData.type === 'compra-venda' ? 'Valor de Venda' : 'Valor do Aluguel'}
              name="rentValue"
              type="number"
              step="0.01"
              value={financial.rentValue || ''}
              onChange={(e) => handleValueChange('rentValue', e.target.value)}
              placeholder="0,00"
              helperText={formatCurrency(financial.rentValue)}
              required
            />
            
            <Input
              label={contractData.type === 'compra-venda' ? 'Valor de Entrada' : 'Caução/Depósito'}
              name="deposit"
              type="number"
              step="0.01"
              value={financial.deposit || ''}
              onChange={(e) => handleValueChange('deposit', e.target.value)}
              placeholder="0,00"
              helperText={formatCurrency(financial.deposit)}
            />
          </div>
        </div>

        {/* Taxas adicionais */}
        <div className="card p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-dark-900">
              Taxas e Encargos Adicionais
            </h3>
            <button
              type="button"
              onClick={addFee}
              className="btn btn-outline btn-sm"
            >
              Adicionar Taxa
            </button>
          </div>

          {(financial.fees || []).map((fee, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 border border-neutral-200 rounded-lg">
              <Input
                label="Nome da Taxa"
                value={fee.name || ''}
                onChange={(e) => handleFeeChange(index, 'name', e.target.value)}
                placeholder="Ex: Taxa de administração"
              />
              
              <Input
                label="Valor (R$)"
                type="number"
                step="0.01"
                value={fee.value || ''}
                onChange={(e) => handleFeeChange(index, 'value', e.target.value)}
                placeholder="0,00"
              />
              
              <Input
                label="Descrição"
                value={fee.description || ''}
                onChange={(e) => handleFeeChange(index, 'description', e.target.value)}
                placeholder="Descrição opcional"
              />
              
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeFee(index)}
                  className="btn btn-danger btn-sm w-full"
                >
                  Remover
                </button>
              </div>
            </div>
          ))}

          {(!financial.fees || financial.fees.length === 0) && (
            <p className="text-neutral-500 text-sm italic">
              Nenhuma taxa adicional cadastrada
            </p>
          )}
        </div>

        {contractData.type === 'locacao' && (
          <div className="card p-6 mb-6">
            <h3 className="text-lg font-semibold text-dark-900 mb-4">
              Condições da Locação
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Select
                label="Dia de Vencimento"
                name="dueDate"
                value={financial.dueDate || 10}
                onChange={handleChange}
                options={dueDateOptions}
              />
              
              <Select
                label="Índice de Reajuste"
                name="adjustment"
                value={financial.adjustment || 'igpm'}
                onChange={handleChange}
                options={adjustmentOptions}
              />
              
              <Select
                label="Duração do Contrato"
                name="contractDuration"
                value={financial.contractDuration || 12}
                onChange={handleChange}
                options={durationOptions}
              />
            </div>
          </div>
        )}

        {/* Resumo financeiro */}
        <div className="card p-6 bg-primary-50 border-primary-200">
          <h3 className="text-lg font-semibold text-primary-800 mb-4">
            Resumo Financeiro
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-primary-700">
                {contractData.type === 'compra-venda' ? 'Valor de Venda:' : 'Valor do Aluguel:'}
              </span>
              <span className="font-semibold text-primary-800">
                {formatCurrency(financial.rentValue)}
              </span>
            </div>
            
            {(financial.fees || []).map((fee, index) => (
              <div key={index} className="flex justify-between items-center text-sm">
                <span className="text-primary-600">{fee.name}:</span>
                <span className="text-primary-700">{formatCurrency(fee.value)}</span>
              </div>
            ))}
            
            <hr className="border-primary-200" />
            
            <div className="flex justify-between items-center text-lg font-bold">
              <span className="text-primary-800">Total Mensal:</span>
              <span className="text-primary-800">{formatCurrency(getTotalValue())}</span>
            </div>
            
            {financial.deposit > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-primary-700">
                  {contractData.type === 'compra-venda' ? 'Entrada:' : 'Caução/Depósito:'}
                </span>
                <span className="font-semibold text-primary-800">
                  {formatCurrency(financial.deposit)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialForm;